import { useCallback, useEffect, useRef, useState } from 'react';
import type { AdminPage } from '../lib/api';

// A list read a page at a time with "Load more" (#73, the admin panel).
// `fetchPage` changes identity when what the list shows changes (a search,
// a filter): the list then starts again from the first page. Answers to an
// earlier search that land late are dropped.

export interface PagedList<T, X> {
  /** Null until the first page lands (or after a reset). */
  items: T[] | null;
  /** Rows the list has in all (for "120 families"). */
  total: number;
  /** The rest of the last page's response (badge counts and the like). */
  extra: Omit<X, keyof AdminPage<T>> | null;
  hasMore: boolean;
  /** The first page failed. */
  error: unknown;
  loadingMore: boolean;
  /** "Load more" failed. */
  moreError: unknown;
  loadMore: () => Promise<void>;
  /**
   * Fetches again as many rows as are on screen, keeping them shown in the
   * meantime: after an edit, so the list keeps its place.
   */
  reload: () => Promise<void>;
  /** Fetches the first page again (Try again). */
  retry: () => void;
}

export function usePagedList<T extends { id: string }, X extends AdminPage<T>>(
  fetchPage: (cursor: string | null) => Promise<X>
): PagedList<T, X> {
  const [items, setItems] = useState<T[] | null>(null);
  const [total, setTotal] = useState(0);
  const [extra, setExtra] = useState<Omit<X, keyof AdminPage<T>> | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);
  // Bumped on every reset: responses for an older generation are dropped.
  const generation = useRef(0);
  const shown = useRef(0);
  shown.current = items?.length ?? 0;

  const apply = useCallback((page: X) => {
    const { items: _items, nextCursor, total: count, ...rest } = page;
    setCursor(nextCursor);
    setTotal(count);
    setExtra(rest as Omit<X, keyof AdminPage<T>>);
  }, []);

  const loadFirst = useCallback(() => {
    const mine = ++generation.current;
    setItems(null);
    setError(null);
    setMoreError(null);
    setCursor(null);
    fetchPage(null).then(
      (page) => {
        if (mine !== generation.current) return;
        apply(page);
        setItems(page.items);
      },
      (err: unknown) => {
        if (mine === generation.current) setError(err ?? true);
      }
    );
  }, [fetchPage, apply]);

  useEffect(() => {
    loadFirst();
  }, [loadFirst]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    const mine = generation.current;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const page = await fetchPage(cursor);
      if (mine !== generation.current) return;
      apply(page);
      setItems((current) => {
        const have = new Set((current ?? []).map((item) => item.id));
        return [
          ...(current ?? []),
          ...page.items.filter((item) => !have.has(item.id)),
        ];
      });
    } catch (err) {
      if (mine === generation.current) setMoreError(err ?? true);
    } finally {
      if (mine === generation.current) setLoadingMore(false);
    }
  }, [cursor, loadingMore, fetchPage, apply]);

  const reload = useCallback(async () => {
    const mine = ++generation.current;
    setLoadingMore(false);
    const want = Math.max(1, shown.current);
    const rows: T[] = [];
    let next: string | null = null;
    let last: X | null = null;
    do {
      const page: X = await fetchPage(next);
      if (mine !== generation.current) return;
      last = page;
      rows.push(...page.items);
      next = page.nextCursor;
    } while (next && rows.length < want);
    if (last) apply(last);
    setItems(rows);
  }, [fetchPage, apply]);

  return {
    items,
    total,
    extra,
    hasMore: cursor !== null,
    error,
    loadingMore,
    moreError,
    loadMore,
    reload,
    retry: loadFirst,
  };
}

/** `value`, once it has stopped changing for `ms` (a search box). */
export function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}
